import { Action, ActionPayload } from "./actions"
import { getVisibleLayers } from "./layers"
import { Field, ImportTask, Layer, LayerType, ManagementTask, ParametersWithFields, SelectedLayerType, Selection, SoilOptixSet } from "./types"
import { startSetParameters, updateParameters } from "./parameters"

export interface SharedState {

    /** All available import tasks */
    imports: ImportTask[]

    /** All available layers */
    layers: Layer[]

    /** All available sets */
    sets: SoilOptixSet[]

    /** Management tasks */
    tasks: ManagementTask[]

    /** Reference data from other modules */
    reference: {
        fields: Field[]
        layerTypes: LayerType[]
    }

    /** Current selection, accounting for all filters */
    selection: Selection

    /** Selected/expanded field */
    selectedFieldKey: string

    /** Selected layer type */
    layerType: SelectedLayerType

    /** Ids of the visible layers, for the current selection and layer type */
    visibleLayers: number[]

    /** Layer viewer options */
    layerViewerOptions: {
        points: boolean
        opacity: number
    }

}

export type State = SharedState & (

    | {
        type: "init" | "index"
    }

    | {
        type: "setParameters",
        parameters: ParametersWithFields
    }

)

export function getInitialState(): State {

    return {

        type: "init",

        imports: [],
        sets: [],
        layers: [],

        tasks: [],

        selection: {
            filters: {
                date: {
                    date: 0,
                    comparison: "in"
                },
                setId: 0
            },
            layers: [],
            fields: [],
            groups: [],
            layerTypes: []
        },

        selectedFieldKey: "",

        layerType: {
            typeId: undefined,
            dynamicScale: false
        },

        visibleLayers: [],

        layerViewerOptions: {
            points: false,
            opacity: 100
        },

        reference: {
            fields: [],
            layerTypes: []
        }

    }

}


const reducers: { [key in Action["type"]]: (state: State, action: Action) => State } = {

    "index": (state: State) => ({
        ...state,
        type: "index"
    }),

    refresh,
    addMgmtTasks,

    setSelection,
    setSelectedField,

    setLayerType,
    setLayerViewerOptions,

    startSetParameters,
    updateParameters

}

export function updateState(state: State, action: Action) {
    const r = reducers[action.type]
    return r ? r(state, action) : state
}


function refresh(state: State, { data }: ActionPayload<"refresh">): State {

    let { imports } = data
    const { selection = state.selection } = data

    imports = imports.sort((a, b) => b.startDate.localeCompare(a.startDate))

    return {

        ...state,

        type: "index",
        ...data,
        imports,

        selection,
        visibleLayers: getVisibleLayers(selection, state.layerType)

    }

}

function addMgmtTasks(state: State, { newTasks }: ActionPayload<"addMgmtTasks">): State {

    const { tasks } = state

    return {
        ...state,
        tasks: tasks.concat(newTasks)
    }

}

function setSelection(state: State, { selection }: ActionPayload<"setSelection">): State {

    return {
        ...state,
        selection,
        visibleLayers: getVisibleLayers(selection, state.layerType)
    }

}

function setSelectedField(state: State, { fieldKey }: ActionPayload<"setSelectedField">): State {

    return {
        ...state,
        selectedFieldKey: fieldKey,
    }

}

function setLayerType(state: State, { layerType }: ActionPayload<"setLayerType">): State {

    return {
        ...state,
        layerType,
        visibleLayers: getVisibleLayers(state.selection, layerType)
    }

}

function setLayerViewerOptions(state: State, { update }: ActionPayload<"setLayerViewerOptions">): State {

    return {
        ...state,
        layerViewerOptions: {
            ...state.layerViewerOptions,
            ...update
        }
    }
}
